/* Amber Oud — shared luxury interactions (lightweight, dependency-free) */
(function () {
  'use strict';

  /* Reveal-on-scroll for any .ao-reveal element */
  function initReveal() {
    var els = document.querySelectorAll('.ao-reveal');
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('ao-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var el = entry.target;
          var delay = el.getAttribute('data-ao-delay');
          if (delay) el.style.transitionDelay = delay + 'ms';
          el.classList.add('ao-in');
          io.unobserve(el);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Header condensed state + scroll progress bar */
  function initHeaderScroll() {
    var last = -1;
    var bar = document.createElement('div');
    bar.className = 'ao-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    function onScroll() {
      var y = window.pageYOffset || document.documentElement.scrollTop;
      var scrolled = y > 40;
      if (scrolled !== last) {
        document.body.classList.toggle('ao-scrolled', scrolled);
        last = scrolled;
      }
      var h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.width = (h > 0 ? (y / h) * 100 : 0) + '%';
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Custom luxury cursor — desktop, fine pointer, motion allowed only */
  function initCursor() {
    if (reduce) return;
    if (!(window.matchMedia && window.matchMedia('(hover: hover)').matches)) return;
    var dot = document.createElement('div');
    dot.className = 'ao-cursor';
    dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);
    var cx = 0, cy = 0, tx = 0, ty = 0, started = false;
    window.addEventListener('mousemove', function (e) {
      tx = e.clientX; ty = e.clientY;
      if (!started) { started = true; cx = tx; cy = ty; dot.classList.add('is-on'); }
    }, { passive: true });
    (function loop() {
      cx += (tx - cx) * 0.22; cy += (ty - cy) * 0.22;
      dot.style.transform = 'translate(' + cx + 'px,' + cy + 'px) translate(-50%,-50%)';
      requestAnimationFrame(loop);
    })();
    var interactive = 'a,button,summary,label[for],[role="button"],[data-cursor],[data-ao-cursor],input:not([type="hidden"]),select,.ao-bs__card,.ao-collections__tile,.ao-gift__card,.ao-social__tile,.swatch,.card-wrapper';
    document.addEventListener('mouseover', function (e) {
      dot.classList.toggle('is-big', !!(e.target.closest && e.target.closest(interactive)));
    });
  }

  /* Draggable horizontal rails ([data-ao-rail]) + progress indicator */
  function initRails(root) {
    (root || document).querySelectorAll('[data-ao-rail]').forEach(function (rail) {
      if (rail.__aoRail) return;
      rail.__aoRail = true;
      var bar = rail.parentElement && rail.parentElement.querySelector('[data-ao-rail-bar]');
      var down = false, sx = 0, sl = 0, moved = false;
      /* Drag-to-scroll without pointer capture, so child <a> clicks navigate natively */
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        down = true; moved = false; sx = e.clientX; sl = rail.scrollLeft;
        rail.classList.add('is-drag');
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - sx;
        if (Math.abs(dx) > 4) moved = true;
        rail.scrollLeft = sl - dx;
      });
      function end() { if (!down) return; down = false; rail.classList.remove('is-drag'); }
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
      /* Only swallow the click if the user actually dragged */
      rail.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      function prog() {
        if (!bar) return;
        var max = rail.scrollWidth - rail.clientWidth;
        var p = max > 0 ? Math.min(1, Math.abs(rail.scrollLeft) / max) : 0;
        bar.style.insetInlineStart = (p * 70) + '%';
      }
      rail.addEventListener('scroll', prog, { passive: true });
      prog();
    });
  }

  /* Desktop hover-to-open for header dropdowns (click still works natively) */
  function initHeaderDropdownHover() {
    if (!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)) return;
    document.querySelectorAll('.header__inline-menu details').forEach(function (d) {
      var li = d.closest('li') || d;
      var t = null;
      li.addEventListener('mouseenter', function () { clearTimeout(t); d.open = true; });
      li.addEventListener('mouseleave', function () { t = setTimeout(function () { d.open = false; }, 120); });
    });
  }

  /* Search "Recently viewed" — localStorage backed, no app required */
  var AO_RECENT_KEY = 'ao_recent_v1';
  var AO_RECENT_MAX = 8;

  var AO_RECENT_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days — bounds staleness of cached commerce data

  function aoReadRecent() {
    try {
      var list = JSON.parse(localStorage.getItem(AO_RECENT_KEY)) || [];
      var cutoff = Date.now() - AO_RECENT_TTL;
      // Entries carry price/availability snapshots — expire them so an old
      // browser session can never resurrect long-stale commerce data.
      return list.filter(function (p) { return p && p.handle && p.t && p.t > cutoff; });
    } catch (e) { return []; }
  }
  function aoWriteRecent(list) {
    try { localStorage.setItem(AO_RECENT_KEY, JSON.stringify(list.slice(0, AO_RECENT_MAX))); } catch (e) {}
  }
  function aoRecordRecent() {
    var item = window.AO_RECENT_ITEM;
    if (!item || !item.handle) return;
    item.t = Date.now();
    var list = aoReadRecent().filter(function (p) { return p.handle !== item.handle; });
    list.unshift(item);
    aoWriteRecent(list);
  }

  /* Keep stored snapshots honest: whenever the shop revalidator (ao-shop.js)
     fetches fresh availability/price for a product, sync the stored entry. */
  document.addEventListener('ao:fresh:product', function (e) {
    var d = e.detail || {};
    if (!d.handle || !d.fresh) return;
    var list = aoReadRecent();
    var changed = false;
    list.forEach(function (p) {
      if (p.handle !== d.handle) return;
      p.available = d.fresh.available;
      if (d.fresh.first) p.variant = d.fresh.first;
      if (d.fresh.minPrice) { p.price = d.fresh.minPrice; }
      if (typeof d.fresh.minCents === 'number') { p.cents = d.fresh.minCents; }
      changed = true;
    });
    if (changed) aoWriteRecent(list);
  });

  function aoEsc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var AO_CART_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M6 2 3 6v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V6l-3-4z"/><path d="M3 6h18"/><path d="M12 10v6M9 13h6"/></svg>';

  function aoCardBtn(p, isAr, variantClass) {
    if (!p.variant || p.available === false) return '';
    var simple = p.simple === true || p.simple === 'true';
    var label = simple ? (isAr ? 'أضف إلى السلة' : 'Add to cart') : (isAr ? 'اختر الخيارات' : 'Choose options');
    return '<button type="button" class="ao-card__add ao-card__add--' + (variantClass || 'over') + (simple ? ' ao-card__add--simple' : '') + '"' +
      ' data-ao-add data-url="' + aoEsc(p.url) + '" data-variant="' + aoEsc(p.variant) + '"' +
      ' data-simple="' + simple + '" data-available="true" aria-label="' + aoEsc(label) + '">' +
      '<span class="ao-card__add-ico" aria-hidden="true">' + AO_CART_SVG + '</span>' +
      '<span class="ao-card__add-label">' + aoEsc(label) + '</span></button>';
  }

  function aoRenderRecent(grid) {
    var list = aoReadRecent();
    var isAr = (document.documentElement.getAttribute('dir') || '').toLowerCase() === 'rtl';
    grid.innerHTML = list.map(function (p) {
      var media = '<div class="ao-search__card-media">' +
        (p.image ? '<img src="' + aoEsc(p.image) + '" alt="" loading="lazy">' : '') +
        aoCardBtn(p, isAr, 'over') + '</div>';
      return '<div class="ao-search__card" data-ao-card data-handle="' + aoEsc(p.handle || '') + '" data-url="' + aoEsc(p.url) + '"' +
        ' data-title="' + aoEsc(p.title) + '" data-price="' + aoEsc(p.price || '') + '"' +
        ' data-price-cents="' + (typeof p.cents === 'number' ? p.cents : '') + '"' +
        ' data-image="' + aoEsc(p.image || '') + '" data-variant="' + aoEsc(p.variant || '') + '"' +
        ' data-simple="' + (p.simple === true || p.simple === 'true') + '" data-available="' + (p.available !== false) + '">' +
        media +
        '<p class="ao-search__card-title">' + aoEsc(p.title || '') + '</p>' +
        (p.price ? '<span class="ao-search__card-price">' + aoEsc(p.price) + '</span>' : '') +
        aoCardBtn(p, isAr, 'inline') +
        '<a class="ao-search__card-link" href="' + aoEsc(p.url) + '" aria-label="' + aoEsc(p.title || '') + '"></a>' +
        '</div>';
    }).join('');
    // Ask the commerce revalidator (ao-shop.js) to fresh-check what we just
    // rendered — the stored snapshots may be days old.
    document.dispatchEvent(new CustomEvent('ao:fresh:scan', { detail: { root: grid } }));
    return list.length;
  }

  function initSearchRecent() {
    aoRecordRecent();
    document.querySelectorAll('.ao-search').forEach(function (modal) {
      var recent = modal.querySelector('[data-ao-recent]');
      var grid = modal.querySelector('[data-ao-recent-grid]');
      var clearBtn = modal.querySelector('[data-ao-recent-clear]');
      var input = modal.querySelector('input[type="search"]');
      var recommend = modal.querySelector('[data-ao-recommend]');
      if (!recent || !grid || !input) return;

      function sync() {
        var typing = input.value.trim().length > 0;
        if (typing) {
          recent.hidden = true;
          if (recommend) recommend.hidden = true;
        } else {
          recent.hidden = aoRenderRecent(grid) === 0;
          if (recommend) recommend.hidden = false;
        }
      }

      input.addEventListener('input', sync);
      input.addEventListener('focus', sync);
      modal.querySelector('details') && modal.addEventListener('toggle', sync, true);
      if (clearBtn) {
        clearBtn.addEventListener('click', function () {
          aoWriteRecent([]);
          recent.hidden = true;
        });
      }
      sync();
    });
  }

  function boot() {
    initReveal();
    initHeaderScroll();
    initCursor();
    initRails();
    initHeaderDropdownHover();
    initSearchRecent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* Re-scan reveals + rails when sections re-render in the theme editor */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal();
    initRails(e.target || document);
  });
})();
